import { useEffect, useRef, useState } from "react";
import { initialOf } from "@/lib/format";

/**
 * The company's logo, or its first letter where it has none or the image
 * does not load. In a job card's top right corner, and beside the company's
 * name in the drawer and on the posting's page.
 *
 * The tile takes the logo's real ratio: 36 px high and as wide as it needs,
 * up to 90 px (72 px on a phone, where the widest logos come out a little
 * shorter), so a wordmark can be read. The catalogue draws each logo with
 * its own margin and ground, so the image is the whole tile. The white is
 * for a logo drawn before that, which has no ground of its own. The letter
 * keeps a 36 px square on the card's own tile.
 *
 * Decorative: the company's name is written beside it, so the image says
 * nothing a screen reader has not just read.
 */
export default function CompanyLogo({ url, company }: { url: string | null; company: string }) {
    // Keyed by the URL, so a card shown again for another posting starts over.
    const [failed, setFailed] = useState<string | null>(null);
    const image = useRef<HTMLImageElement>(null);
    // On a server's page the image loads before React is there to hear it fail.
    useEffect(() => {
        const shown = image.current;
        if (shown?.complete && shown.naturalWidth === 0) setFailed(url);
    }, [url]);
    if (url === null || failed === url) {
        return (
            <span
                aria-hidden="true"
                data-testid="company-logo"
                className="grid size-9 shrink-0 place-items-center rounded-[9px] border border-line bg-card-raised"
            >
                <span className="font-display text-[14px] font-semibold text-brand-ink">
                    {initialOf(company)}
                </span>
            </span>
        );
    }
    return (
        <span
            aria-hidden="true"
            data-testid="company-logo"
            className="block shrink-0 overflow-hidden rounded-[9px] border border-line bg-white"
        >
            <img
                ref={image}
                src={url}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setFailed(url)}
                className="block max-h-[34px] min-h-7 min-w-[34px] max-w-[88px] max-[760px]:max-w-[70px]"
            />
        </span>
    );
}
